import type { ReactNode } from "react";
import classNames from "classnames";
import { OrganizationAvatar } from "@components/avatar/OrganizationAvatar";
import { Avatar } from "@design-system/Avatar/Avatar";
import { AvatarGroup } from "@design-system/Avatar/AvatarGroup";
import { Tag } from "@design-system/Tag/Tag";
import { VStack } from "@design-system/VStack/VStack";
import { CalendarIcon } from "@icons";
import styles from "./TagPlayground.module.scss";

const contentHeaders = [
    "text",
    "icon + text",
    "avatar + text",
    "avatar only",
    "group only",
    "square only",
] as const;

/** Each tag size with the avatar 4px shorter than it, so every inset is 2px. */
const contentSizes = [
    { tagSize: "sm", avatarSize: "xs" },
    { tagSize: "md", avatarSize: "sm" },
    { tagSize: "lg", avatarSize: "md" },
] as const;

const kickOrganization = { name: "Kick", logoUrl: null };

const people = [
    { id: "matt", name: "Matt Peterson", color: "var(--color-ocean-blue-200)" },
    { id: "ana", name: "Ana Park", color: "var(--color-sea-green-200)" },
    { id: "cy", name: "Cy Lee", color: "var(--color-dusk-purple-200)" },
] as const;

type ContentSize = (typeof contentSizes)[number];

/** An outline tag at each size; lg has only the primary colour. */
const ContentTag = ({
    tagSize,
    leftIcon,
    children,
}: {
    tagSize: ContentSize["tagSize"];
    leftIcon?: ReactNode;
    children: ReactNode;
}) => {
    switch (tagSize) {
        case "sm":
            return (
                <Tag size="sm" color="neutral" outline leftIcon={leftIcon}>
                    {children}
                </Tag>
            );
        case "md":
            return (
                <Tag size="md" color="neutral" outline leftIcon={leftIcon}>
                    {children}
                </Tag>
            );
        case "lg":
            return (
                <Tag size="lg" outline leftIcon={leftIcon}>
                    {children}
                </Tag>
            );
    }
};

const ContentRow = ({ tagSize, avatarSize }: ContentSize) => {
    const [matt] = people;
    const cells = [
        <ContentTag key="text" tagSize={tagSize}>
            Close
        </ContentTag>,
        <ContentTag key="icon" tagSize={tagSize} leftIcon={<CalendarIcon />}>
            Oct 16
        </ContentTag>,
        <ContentTag
            key="avatar-text"
            tagSize={tagSize}
            leftIcon={
                <Avatar
                    name={matt.name}
                    color={matt.color}
                    size={avatarSize}
                    aria-hidden
                />
            }
        >
            Matt
        </ContentTag>,
        <ContentTag key="avatar" tagSize={tagSize}>
            <Avatar
                name={matt.name}
                variant="outline"
                tone="neutral"
                size={avatarSize}
            />
        </ContentTag>,
        <ContentTag key="group" tagSize={tagSize}>
            <AvatarGroup
                items={people}
                getKey={(person) => person.id}
                getLabel={(person) => person.name}
                renderAvatar={(person) => (
                    <Avatar name={person.name} color={person.color} />
                )}
                size={avatarSize}
            />
        </ContentTag>,
        <ContentTag
            key="square"
            tagSize={tagSize}
            leftIcon={
                <OrganizationAvatar
                    organization={kickOrganization}
                    size={avatarSize}
                />
            }
        >
            {null}
        </ContentTag>,
    ];
    return (
        <>
            <span className={styles.rowLabel}>{tagSize}</span>
            {cells.map((cell) => (
                <span key={cell.key} className={styles.cell}>
                    {cell}
                </span>
            ))}
        </>
    );
};

/**
 * Tag padding picked from its content: text and icons keep the size's
 * padding; a leading avatar, group or square sits as far from the left edge
 * as from the top, and alone it sits centred. Avatar only and group only
 * pass the box as children; square only passes it as `leftIcon`.
 */
export const TagContentPaddingDemo = () => (
    <VStack className={styles.section} gap={16} padding={24}>
        <VStack gap={4}>
            <h2 className={styles.sectionTitle}>Content padding</h2>
            <span className={styles.sectionDescription}>
                Avatars, groups and squares inset (height - box) / 2; text and
                icons keep the size&apos;s padding.
            </span>
        </VStack>
        <div className={classNames(styles.grid, styles.grid6)}>
            <span className={styles.columnHeader}>size</span>
            {contentHeaders.map((header) => (
                <span key={header} className={styles.columnHeader}>
                    {header}
                </span>
            ))}
            {contentSizes.map((size) => (
                <ContentRow key={size.tagSize} {...size} />
            ))}
        </div>
    </VStack>
);
